import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { useAuth } from '../../lib/auth';
import { db, functions } from '../../lib/firebase';
import { EventContext } from '../../event/eventContext';
import { eventDoc, getEventId } from '../../event/paths';
import { serverMessageOr } from '../../utils/authErrors';
import { BLOCKING_MODES, abnLooksRight, gateMode, isPaysPath, startRetainerFlowOnce } from '../../utils/retainerGate';
import { buildRetainerJotformScriptSrc, jotformFormIdFromEmbedUrl, readJotformSubmitMessage } from '../../utils/retainerJotform';

// Retainer ABN flow gate (RETAINER_ABN_SUPPLIER_PLAN.md 5.8, owner D1).
//
// Mounted once in the Dashboard layout. Self-gating:
//   - never while viewing as someone else, never for signed-out users;
//   - on a brand that has used the flow, every protected page waits (neutral loading) until
//     this host's own flow doc is known from the server, then locks the page full screen
//     while the flow is unfinished;
//   - only /pays starts a flow.
// The server is the only writer of the flow; this component never writes Firestore.

const AUTO_CHECK_MS = 10 * 1000;
const STILL_CHECKING_MS = 20 * 1000;

const call = (name, data) => httpsCallable(functions, name)({ eventId: getEventId(), ...(data || {}) }).then((r) => r.data);

function isJotformOrigin(origin) {
  try {
    const host = new URL(origin).hostname.toLowerCase();
    return host === 'jotform.com' || host.endsWith('.jotform.com');
  } catch {
    return false;
  }
}

function JotformEmbed({ src, expectFormId, onSubmitted }) {
  const ref = useRef(null);
  const submittedRef = useRef(onSubmitted);
  submittedRef.current = onSubmitted;
  // Jotform's form can take several seconds to appear: say so until its iframe shows up.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const box = ref.current;
    if (!box || !src) return undefined;
    setLoaded(false);
    box.innerHTML = '';
    // Loaded = the iframe finished loading, or Jotform sent its first message (it posts
    // setHeight once the form has rendered). Not just "an iframe was inserted".
    const observer = new MutationObserver(() => {
      const frame = box.querySelector('iframe');
      if (frame) {
        frame.addEventListener('load', () => setLoaded(true), { once: true });
        observer.disconnect();
      }
    });
    observer.observe(box, { childList: true, subtree: true });
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    box.appendChild(script);
    const onMessage = (event) => {
      if (isJotformOrigin(event.origin)) setLoaded(true);
      const msg = readJotformSubmitMessage(event);
      if (!msg) return;
      if (msg.formID && expectFormId && msg.formID !== expectFormId) return;
      submittedRef.current(msg);
    };
    window.addEventListener('message', onMessage);
    return () => {
      observer.disconnect();
      window.removeEventListener('message', onMessage);
      box.innerHTML = '';
    };
  }, [src, expectFormId]);

  return (
    <>
      {!loaded && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading the form...</Typography>
        </Stack>
      )}
      <Box ref={ref} sx={{ minHeight: 400 }} />
    </>
  );
}

function Screen({ title, children, help = true }) {
  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', width: '100%', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      {title && (
        <Typography variant="h3" sx={{ mb: 2 }}>
          {title}
        </Typography>
      )}
      <Stack spacing={2}>{children}</Stack>
      {help && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
          Stuck? Contact your manager.
        </Typography>
      )}
    </Box>
  );
}

// The lock itself: full screen, no close, no Escape, no fade-in. Each new screen starts at the top.
function GateDialog({ mode, screenKey, render }) {
  const contentRef = useRef(null);
  useEffect(() => {
    const paper = contentRef.current && contentRef.current.closest('.MuiDialog-paper');
    if (paper) paper.scrollTop = 0;
  }, [screenKey]);
  if (mode === 'none') return null;
  return (
    <Dialog
      open
      fullScreen
      onClose={() => {}}
      disableEscapeKeyDown
      // A lock appears at once: no fade-in during which the page behind shows through.
      transitionDuration={0}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 20 }}
      PaperProps={{ sx: { bgcolor: 'background.default' } }}
      aria-label="Retainer paperwork"
    >
      <div ref={contentRef}>{render()}</div>
    </Dialog>
  );
}

export default function RetainerFlowGate({ onActiveChange }) {
  const { user, realProfile, impersonatedProfile, loading: authLoading } = useAuth();
  const ev = useContext(EventContext) || {};
  const location = useLocation();

  const eventId = ev.eventId;
  const rf = ev.retainerForm || {};
  const brandName = ev.brand?.name || '';
  const uid = user?.uid || '';
  const userDocId = realProfile?.id || '';
  const identity = `${uid}|${eventId}|${userDocId}`;
  const impersonating = Boolean(impersonatedProfile);
  const watchFlow = Boolean(uid && userDocId && !impersonating && rf.abnFlowEverEnabled === true);

  // ---- this host's flow doc (server-written; read-only here) ----
  const [flow, setFlow] = useState({ key: '', state: 'unknown', data: null, fromCache: true });
  const [flowRetry, setFlowRetry] = useState(0);
  useEffect(() => {
    if (!watchFlow) return undefined;
    let live = true;
    setFlow({ key: identity, state: 'unknown', data: null, fromCache: true });
    const unsub = onSnapshot(
      eventDoc(db, 'users', userDocId, 'private', 'retainerFlow'),
      { includeMetadataChanges: true },
      (snap) => {
        if (!live) return;
        const fromCache = snap.metadata.fromCache;
        if (snap.exists()) setFlow({ key: identity, state: 'present', data: snap.data(), fromCache });
        else if (!fromCache) setFlow({ key: identity, state: 'absent', data: null, fromCache });
        // a cache-only miss is not "absent": stay unknown
      },
      () => {
        if (!live) return;
        // Keep a known unfinished flow locked; otherwise block with Retry.
        setFlow((cur) =>
          cur.key === identity && cur.state === 'present' ? cur : { key: identity, state: 'error', data: null, fromCache: true }
        );
      }
    );
    return () => {
      live = false;
      unsub();
    };
  }, [watchFlow, identity, userDocId, flowRetry]);
  const flowNow = flow.key === identity ? flow : { state: 'unknown', data: null, fromCache: true };
  const step = flowNow.data?.step || null;

  // ---- per-identity session state ----
  const [session, setSession] = useState({ key: identity, startState: 'idle', seenUnfinished: false, finished: false });
  const s = session.key === identity ? session : { key: identity, startState: 'idle', seenUnfinished: false, finished: false };
  const patchSession = useCallback(
    (p) =>
      setSession((cur) => ({
        ...(cur.key === identity ? cur : { key: identity, startState: 'idle', seenUnfinished: false, finished: false }),
        ...p
      })),
    [identity]
  );

  useEffect(() => {
    if (flowNow.state !== 'present') return;
    if (step && step !== 'done' && !s.seenUnfinished) patchSession({ seenUnfinished: true });
    if (step === 'done' && s.seenUnfinished && !s.finished) patchSession({ finished: true, seenUnfinished: false });
  }, [flowNow.state, step, s.seenUnfinished, s.finished, patchSession]);

  const mode = gateMode({
    authLoading: user === undefined || authLoading === true,
    hasUser: Boolean(uid),
    impersonating,
    configStatus: ev.configStatus,
    everEnabled: rf.abnFlowEverEnabled,
    enabled: rf.abnFlowEnabled,
    hasProfile: Boolean(userDocId),
    flow: { state: flowNow.state, step },
    isPays: isPaysPath(location.pathname),
    flagFalse: realProfile?.retainerJotform === false,
    startState: s.startState === 'starting' ? 'idle' : s.startState,
    finishedThisSession: s.finished
  });

  useEffect(() => {
    if (onActiveChange) onActiveChange(BLOCKING_MODES.has(mode));
  }, [mode, onActiveChange]);

  // ---- start (only on /pays, see gateMode) ----
  // One request per attempt (startState 'idle' -> 'starting'). Its answer is applied by
  // startRetainerFlowOnce only if the session still belongs to the same identity, so it is
  // never cancelled by this effect's own state change (Code review R1 M1), and a late answer
  // for a previous user or brand is dropped.
  useEffect(() => {
    if (mode !== 'starting' || s.startState !== 'idle') return;
    startRetainerFlowOnce({ key: identity, start: () => call('startRetainerFlow'), setSession });
  }, [mode, s.startState, identity]);

  // ---- offline banner ----
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);

  // ---- per-step UI state, reset whenever the step changes ----
  const [ui, setUi] = useState({ step: null, showForm: false, abnOpen: false, abn: '', busy: false, error: '' });
  const u = ui.step === step ? ui : { step, showForm: false, abnOpen: false, abn: '', busy: false, error: '' };
  const setU = (p) => setUi({ ...u, ...p });

  // ---- after Submit: checking, one automatic hinted check, then "Check again" ----
  const [checking, setChecking] = useState(null); // { step, at, formID, submissionID, auto, still, note }
  const activeCheck = checking && checking.step === step ? checking : null;
  useEffect(() => {
    if (!activeCheck) return undefined;
    const t1 = activeCheck.auto
      ? null
      : setTimeout(
          () => {
            setChecking((c) => (c ? { ...c, auto: true } : c));
            runCheck({ submissionIdHint: activeCheck.submissionID || undefined, formIdHint: activeCheck.formID || undefined }, true);
          },
          Math.max(0, activeCheck.at + AUTO_CHECK_MS - Date.now())
        );
    const t2 = activeCheck.still
      ? null
      : setTimeout(() => setChecking((c) => (c ? { ...c, still: true } : c)), Math.max(0, activeCheck.at + STILL_CHECKING_MS - Date.now()));
    return () => {
      if (t1) clearTimeout(t1);
      if (t2) clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCheck?.at, activeCheck?.auto, activeCheck?.still]);

  const runCheck = async (hints, quiet) => {
    try {
      const r = await call('checkRetainerSubmission', hints);
      const note =
        r?.result === 'wait'
          ? 'Please give it a few more seconds, then try again.'
          : r?.result === 'not_found_yet'
            ? "We haven't received your form yet. If you just sent it, give it a minute and try again."
            : r?.result === 'api_error'
              ? "We couldn't check just now. Please try again in a minute."
              : r?.result === 'too_many'
                ? 'Please contact your manager to finish your paperwork.'
                : '';
      if (!quiet || r?.result === 'too_many') setChecking((c) => (c ? { ...c, note } : c));
    } catch (err) {
      if (!quiet)
        setChecking((c) => (c ? { ...c, note: serverMessageOr(err, "We couldn't check just now. Please try again in a minute.") } : c));
    }
  };

  const onSubmitted = (msg) =>
    setChecking({ step, at: Date.now(), formID: msg.formID, submissionID: msg.submissionID, auto: false, still: false, note: '' });

  const hidden = useMemo(
    () =>
      flowNow.data?.flowToken && eventId && userDocId ? { hostRef: `${eventId}/${userDocId}`, flowToken: flowNow.data.flowToken } : null,
    [flowNow.data?.flowToken, eventId, userDocId]
  );
  const snapshot = flowNow.data?.snapshot || {};
  const rctiSrc = useMemo(
    () => (hidden ? buildRetainerJotformScriptSrc(snapshot.rctiEmbedUrl, realProfile, hidden) : ''),
    [hidden, snapshot.rctiEmbedUrl, realProfile]
  );
  const supplierSrc = useMemo(
    () => (hidden ? buildRetainerJotformScriptSrc(snapshot.supplierEmbedUrl, realProfile, hidden) : ''),
    [hidden, snapshot.supplierEmbedUrl, realProfile]
  );

  return (
    <GateDialog
      mode={mode}
      screenKey={`${mode}|${step}|${activeCheck ? activeCheck.at : ''}|${u.showForm}|${u.abnOpen}`}
      render={() => renderBody()}
    />
  );

  function renderBody() {
    if (mode === 'none') return null;

    const choose = async (path) => {
      setU({ busy: true, error: '' });
      try {
        await call('chooseRetainerPath', { path });
      } catch (err) {
        setU({ busy: false, error: serverMessageOr(err, "That didn't work. Please try again.") });
      }
    };
    const saveAbn = async () => {
      if (!abnLooksRight(u.abn)) {
        setU({ error: "That doesn't look like a valid ABN. Please check it and try again." });
        return;
      }
      setU({ busy: true, error: '' });
      try {
        await call('submitRetainerAbn', { abn: u.abn });
      } catch (err) {
        setU({ busy: false, error: serverMessageOr(err, "We couldn't save that. Please try again.") });
      }
    };

    const welcomeTitle = brandName ? `Welcome to the ${brandName} Team` : 'Welcome to the Team';
    const problem = flowNow.data?.problem || null;

    let body;
    if (mode === 'loading') {
      body = (
        <Screen help={false}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 10, justifyContent: 'center' }}>
            <CircularProgress size={24} />
            <Typography>Loading...</Typography>
          </Stack>
        </Screen>
      );
    } else if (mode === 'config_error' || mode === 'flow_error') {
      body = (
        <Screen title="We couldn't load your details">
          <Typography>Please check your connection and try again.</Typography>
          <Box>
            <Button
              variant="contained"
              onClick={() => (mode === 'config_error' ? ev.retryConfig && ev.retryConfig() : setFlowRetry((n) => n + 1))}
            >
              Try again
            </Button>
          </Box>
        </Screen>
      );
    } else if (mode === 'starting' || mode === 'start_failed') {
      body = (
        <Screen title={welcomeTitle}>
          <Typography>
            Before we can pay your weekly retainer, you need to complete a one time RCTI authorisation form and provide your bank account
            details.
          </Typography>
          {mode === 'starting' ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={20} />
              <Typography>Getting your form ready...</Typography>
            </Stack>
          ) : (
            <>
              <Alert severity="error">We couldn&apos;t get your form ready. Please try again.</Alert>
              <Box>
                <Button variant="contained" onClick={() => patchSession({ startState: 'idle' })}>
                  Try again
                </Button>
              </Box>
            </>
          )}
        </Screen>
      );
    } else if (mode === 'success') {
      body = (
        <Screen title="Thanks, you're all set" help={false}>
          <Typography>Your retainer paperwork is complete. Your payouts will now be processed automatically.</Typography>
          <Box>
            <Button variant="contained" onClick={() => patchSession({ finished: false })}>
              Done
            </Button>
          </Box>
        </Screen>
      );
    } else {
      // mode === 'flow'
      const banner = !online ? <Alert severity="warning">You&apos;re offline. Reconnect to carry on.</Alert> : null;
      const problemNote =
        problem === 'abn_question_missing' ? (
          <Alert severity="warning">Something on our side isn&apos;t set up right. Please contact management.</Alert>
        ) : problem === 'too_many_submissions' ? (
          <Alert severity="warning">Please contact your manager to finish your paperwork.</Alert>
        ) : null;
      const checkingNote = activeCheck ? (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} />
            <Typography>{activeCheck.still ? 'Still checking...' : 'Thanks, just checking your details...'}</Typography>
          </Stack>
          {activeCheck.note && <Alert severity="info">{activeCheck.note}</Alert>}
          {activeCheck.still && (
            <Box>
              <Button
                variant="outlined"
                onClick={() =>
                  runCheck({ submissionIdHint: activeCheck.submissionID || undefined, formIdHint: activeCheck.formID || undefined }, false)
                }
              >
                Check again
              </Button>
            </Box>
          )}
        </Stack>
      ) : null;

      if (step === 'rcti') {
        body = (
          <Screen title={u.showForm || activeCheck ? 'RCTI authorisation and bank details' : welcomeTitle}>
            {banner}
            {problemNote}
            {!u.showForm && !activeCheck ? (
              <>
                <Typography>
                  Before we can pay your weekly retainer, you need to complete a one time RCTI authorisation form and provide your bank
                  account details.
                </Typography>
                <Typography>This only takes a couple of minutes and only needs to be done once.</Typography>
                <Box>
                  <Button variant="contained" onClick={() => setU({ showForm: true })}>
                    Okay, show me the form
                  </Button>
                </Box>
              </>
            ) : activeCheck ? (
              checkingNote
            ) : (
              <JotformEmbed src={rctiSrc} expectFormId={jotformFormIdFromEmbedUrl(snapshot.rctiEmbedUrl)} onSubmitted={onSubmitted} />
            )}
          </Screen>
        );
      } else if (step === 'abn_missing') {
        const reason = flowNow.data?.abnRejectedReason;
        body = (
          <Screen title="Thanks for filling out the form">
            {banner}
            <Typography>
              {reason === 'format'
                ? "The ABN you entered doesn't look right, and we need either your ABN or a supplier form before we can pay you."
                : 'You left your ABN blank, and we need either your ABN or a supplier form before we can pay you.'}
            </Typography>
            {u.abnOpen ? (
              <Stack spacing={2} sx={{ maxWidth: 360 }}>
                <TextField
                  label="ABN"
                  value={u.abn}
                  onChange={(e) => setU({ abn: e.target.value, error: '' })}
                  inputProps={{ inputMode: 'numeric', autoComplete: 'off' }}
                  autoFocus
                />
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={saveAbn} disabled={u.busy}>
                    {u.busy ? 'Saving...' : 'Save my ABN'}
                  </Button>
                  <Button onClick={() => setU({ abnOpen: false, error: '' })} disabled={u.busy}>
                    Back
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" onClick={() => setU({ abnOpen: true })} disabled={u.busy}>
                  I have an ABN
                </Button>
                <Button variant="outlined" onClick={() => choose('supplier')} disabled={u.busy}>
                  I don&apos;t have an ABN
                </Button>
              </Stack>
            )}
            {u.error && <Alert severity="error">{u.error}</Alert>}
          </Screen>
        );
      } else {
        // step === 'supplier'
        body = (
          <Screen title="Supplier form">
            {banner}
            {problemNote}
            {activeCheck ? (
              checkingNote
            ) : (
              <>
                <Typography>This form is for people who don&apos;t have an ABN. Please fill it out and press Submit.</Typography>
                <Box>
                  <Link component="button" type="button" onClick={() => choose('abn')} disabled={u.busy}>
                    Back, I have an ABN after all
                  </Link>
                </Box>
                <JotformEmbed
                  src={supplierSrc}
                  expectFormId={jotformFormIdFromEmbedUrl(snapshot.supplierEmbedUrl)}
                  onSubmitted={onSubmitted}
                />
              </>
            )}
            {u.error && <Alert severity="error">{u.error}</Alert>}
          </Screen>
        );
      }
    }

    return body;
  }
}
